import { supabase } from './supabaseClient';

/** USD/month fallback when the subscription_plans row is unavailable. Override with VITE_NEXUS_HOSTING_MONTHLY_USD. */
export function getNexusWebsiteHostingMonthlyUsd(): number {
  const raw = import.meta.env.VITE_NEXUS_HOSTING_MONTHLY_USD;
  const n = typeof raw === 'string' ? Number.parseFloat(raw) : Number.NaN;
  return Number.isFinite(n) && n >= 0 ? n : 14.99;
}

let cachedHostingMonthlyUsd: number | null = null;

/**
 * Hosting display price from the subscription_plans 'nexus-hosting' row — the
 * same source of truth the checkout uses — with the env/hardcoded value as
 * fallback. Cached for the session.
 */
export async function fetchNexusHostingMonthlyUsd(): Promise<number> {
  if (cachedHostingMonthlyUsd != null) return cachedHostingMonthlyUsd;
  try {
    const { data } = await supabase
      .from('subscription_plans')
      .select('plan_price')
      .eq('plan_id', 'nexus-hosting')
      .maybeSingle();
    const price = data?.plan_price;
    if (typeof price === 'number' && Number.isFinite(price) && price >= 0) {
      cachedHostingMonthlyUsd = price;
      return price;
    }
  } catch {
    // fall through to static fallback
  }
  cachedHostingMonthlyUsd = getNexusWebsiteHostingMonthlyUsd();
  return cachedHostingMonthlyUsd;
}

export function isNexusHostingActive(status: string | null | undefined): boolean {
  const s = (status || '').toLowerCase();
  return s === 'active' || s === 'trialing';
}

/**
 * Monthly USD to show for website hosting when a Stripe hosting sub exists and is still billed
 * (active, trialing, or payment recovery). Used by Super Admin list totals.
 * Pass `monthlyUsd` (e.g. from the subscription_plans row) to avoid the static fallback.
 */
export function getNexusHostingMonthlyBillableUsd(
  stripeSubId: string | null | undefined,
  status: string | null | undefined,
  monthlyUsd?: number,
): number {
  if (!(stripeSubId || '').toString().trim()) return 0;
  const s = (status || '').toLowerCase();
  if (s === 'active' || s === 'trialing' || s === 'past_due' || s === 'unpaid') {
    return typeof monthlyUsd === 'number' && Number.isFinite(monthlyUsd) && monthlyUsd >= 0
      ? monthlyUsd
      : getNexusWebsiteHostingMonthlyUsd();
  }
  return 0;
}

/** Same shape as platform `StripeSubscriptionDetails` from SubscriptionContext (for billing UI). */
export interface NexusHostingSubscriptionDetails {
  id: string;
  status: string;
  planName: string;
  planId: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  renewalDate: string;
  amount: number;
  currency: string;
  interval: string;
  customerEmail: string;
}

export async function fetchNexusHostingStripeDetails(
  businessId: string,
): Promise<NexusHostingSubscriptionDetails | null> {
  const { data, error } = await supabase.functions.invoke('get-stripe-subscription', {
    body: { businessId, subscriptionKind: 'nexus_hosting' },
  });
  if (error) {
    console.warn('fetchNexusHostingStripeDetails:', error.message);
    return null;
  }
  const payload = data as {
    hostingSubscription?: NexusHostingSubscriptionDetails | null;
    message?: string;
  };
  const sub = payload?.hostingSubscription;
  if (!sub || typeof sub !== 'object' || typeof sub.id !== 'string' || !sub.id.trim()) {
    if (payload?.message) {
      console.warn('get-stripe-subscription (hosting):', payload.message);
    }
    return null;
  }
  return sub;
}

export async function createNexusHostingCheckout(params: {
  businessId: string;
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string;
  /** Set after the user confirms the card-on-file charge (two-phase flow). */
  confirmCardOnFile?: boolean;
}) {
  const body: Record<string, unknown> = {
    action: 'create-nexus-hosting-checkout',
    businessId: params.businessId,
    successUrl: params.successUrl,
    cancelUrl: params.cancelUrl,
  };
  if (params.customerEmail?.trim()) {
    body.customerEmail = params.customerEmail.trim();
  }
  if (params.confirmCardOnFile) {
    body.confirmCardOnFile = true;
  }
  return supabase.functions.invoke('stripe-subscription', { body });
}

/** "visa …4242 will be charged $14.99/month" — confirm-dialog text for the card-on-file step. */
export function describeCardCharge(d: {
  card?: { brand?: string; last4?: string };
  amount?: number;
  currency?: string;
  interval?: string;
}): string {
  const brand = (d.card?.brand || 'card').toUpperCase();
  const last4 = d.card?.last4 || '••••';
  const price =
    typeof d.amount === 'number' && d.amount > 0
      ? `$${d.amount.toFixed(2)}${d.currency && d.currency.toLowerCase() !== 'usd' ? ` ${d.currency.toUpperCase()}` : ''}/${d.interval || 'month'}`
      : 'the hosting subscription price';
  return `${brand} ending in ${last4} will be charged ${price} now.`;
}
